import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { useTableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/useTableColumnSettingsProvider";

import { Box } from "@design-system/Box/Box";
import { Form } from "@design-system/Form/Form";
import { Icon } from "@design-system/Icon/Icon";
import { IconButton } from "@design-system/IconButton/IconButton";
import { Text } from "@design-system/Text/Text";

type Props = {
  humanReadableNames: Record<string, string>;
  defaultColumnSettings: Record<string, boolean>;
  orderingConfirmed: boolean;
  orderReset: boolean;
  onClose: () => void;
};

export const SortableList = ({
  humanReadableNames,
  defaultColumnSettings,
  orderingConfirmed = false,
  orderReset = false,
  onClose,
}: Props) => {
  const {
    columnSettings,
    updateColumnSettings,
    columnOrder,
    updateColumnOrder,
  } = useTableColumnSettingsProvider();
  const { handleSubmit, reset, getValues } = useForm({
    defaultValues: columnSettings,
  });

  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [draggedOverDropZone, setDraggedOverDropZone] = useState<number | null>(
    null,
  );
  const [localColumnOrder, setLocalColumnOrder] = useState<string[]>(() =>
    columnOrder.length > 0 ? [...columnOrder] : Object.keys(columnSettings),
  );

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
    setDraggedOverDropZone(null);
  }, []);

  const handleFormSubmit = useCallback(
    (data: Record<string, boolean>) => {
      updateColumnSettings(data);
      updateColumnOrder(localColumnOrder as string[]);
    },
    [localColumnOrder, updateColumnOrder, updateColumnSettings],
  );

  const handleReset = useCallback(() => {
    const defaultOrder = Object.keys(defaultColumnSettings);
    // Update form state
    reset(defaultColumnSettings);

    // Update column settings and order
    updateColumnSettings(defaultColumnSettings);
    updateColumnOrder(defaultOrder);

    // Update local state
    setLocalColumnOrder(defaultOrder);
    setDraggedItem(null);
    setDraggedOverDropZone(null);

    onClose();
  }, [
    reset,
    defaultColumnSettings,
    updateColumnSettings,
    updateColumnOrder,
    setLocalColumnOrder,
    setDraggedItem,
    setDraggedOverDropZone,
    onClose,
  ]);

  useEffect(() => {
    if (orderingConfirmed) {
      handleSubmit(handleFormSubmit)();
      onClose();
    }
  }, [orderingConfirmed, handleSubmit, onClose, handleFormSubmit]);

  useEffect(() => {
    if (orderReset) {
      handleReset();
    }
  }, [orderReset, handleReset]);

  // Global drag end recovery - clean up if drag ends outside our control
  useEffect(() => {
    const handleWindowDragEnd = () => {
      if (draggedItem) {
        handleDragEnd();
      }
    };

    if (draggedItem) {
      window.addEventListener("dragend", handleWindowDragEnd);
    }

    return () => {
      window.removeEventListener("dragend", handleWindowDragEnd);
    };
  }, [draggedItem, handleDragEnd]);

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    columnId: string,
  ) => {
    setDraggedItem(columnId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.currentTarget.outerHTML);
    // Hide the default drag ghost since we show position with placeholder
    const emptyImg = new Image();
    emptyImg.src =
      "data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=";
    e.dataTransfer.setDragImage(emptyImg, 0, 0);
  };

  const handleDropZoneDrop = (
    e: React.DragEvent<HTMLDivElement>,
    dropZoneIndex: number,
  ) => {
    e.preventDefault();

    if (!draggedItem) {
      return;
    }

    const newOrder = [...localColumnOrder];
    const draggedIndex = newOrder.indexOf(draggedItem);

    // Remove dragged item from current position
    newOrder.splice(draggedIndex, 1);

    // Insert at new position
    newOrder.splice(dropZoneIndex, 0, draggedItem);

    setLocalColumnOrder(newOrder);

    // Update form state to reflect new order
    const currentFormValues = getValues();
    reset(currentFormValues);

    // Manually call drag end to ensure complete cleanup
    handleDragEnd();

    // Extra safety: ensure state is cleared even if something goes wrong
    setTimeout(() => {
      setDraggedItem(null);
      setDraggedOverDropZone(null);
    }, 100);
  };

  // Global cleanup handler for any missed drop events
  const handleGlobalDrop = (e: React.DragEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (draggedItem && draggedOverDropZone !== null) {
      // If we have a valid placeholder position, use it for the drop
      // Create a synthetic event for the drop handler
      const syntheticEvent = e as unknown as React.DragEvent<HTMLDivElement>;
      handleDropZoneDrop(syntheticEvent, draggedOverDropZone);
    } else if (draggedItem) {
      // Otherwise just clean up the state without reordering
      handleDragEnd();
      setTimeout(() => {
        setDraggedItem(null);
        setDraggedOverDropZone(null);
      }, 50);
    }
  };

  return (
    <Form
      onSubmit={handleSubmit(handleFormSubmit)}
      style={{ gap: "4px", paddingBottom: "20px" }}
      onDrop={handleGlobalDrop}
      onDragOver={(e) => {
        e.preventDefault();
        // Allow dropping at the end of the list
        if (draggedItem) {
          const formRect = e.currentTarget.getBoundingClientRect();
          const lastItemBottom = formRect.bottom - 40; // Reserve space for end drop
          if (e.clientY > lastItemBottom) {
            setDraggedOverDropZone(localColumnOrder.length);
          }
        }
      }}
      onDragLeave={(e) => {
        // Clear drag state if we're leaving the container entirely
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setDraggedOverDropZone(null);

          // Add a recovery timeout in case drag state gets stuck
          setTimeout(() => {
            if (draggedItem && draggedOverDropZone === null) {
              // If we have a dragged item but no drop zone after leaving,
              // user probably dragged outside - clear the state
              handleDragEnd();
            }
          }, 1000);
        }
      }}
    >
      {localColumnOrder.map((columnId, index) => {
        const isDraggedItem = draggedItem === columnId;
        const draggedIndex = draggedItem
          ? localColumnOrder.indexOf(draggedItem)
          : -1;

        // Show placeholder before this item if this is the drop zone
        const showPlaceholderBefore =
          draggedItem &&
          draggedOverDropZone === index &&
          draggedIndex !== index &&
          draggedIndex !== index - 1;

        return (
          <React.Fragment key={columnId}>
            {/* Show placeholder where item will be dropped */}
            {showPlaceholderBefore && (
              <div
                style={{
                  padding: "6px 8px",
                  margin: "0 0 4px 0",
                  borderRadius: "4px",
                  backgroundColor: "var(--background-default-secondary)",
                  border: "2px dashed var(--border-default-primary)",
                  height: "36px",
                  opacity: 0.5,
                  pointerEvents: "none",
                }}
              />
            )}

            {/* Render the actual item */}
            <div
              style={{
                opacity: isDraggedItem ? 0.3 : 1,
                marginBottom: "4px",
              }}
            >
              <div
                draggable={!isDraggedItem}
                onDragStart={(e) => handleDragStart(e, columnId)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (draggedItem && draggedItem !== columnId) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const relativeY = e.clientY - rect.top;
                    const threshold = rect.height * 0.5;

                    // Only update if we're clearly in the top or bottom half
                    if (relativeY < threshold) {
                      // Insert before this item
                      if (draggedOverDropZone !== index) {
                        setDraggedOverDropZone(index);
                      }
                    } else {
                      // Insert after this item
                      if (draggedOverDropZone !== index + 1) {
                        setDraggedOverDropZone(index + 1);
                      }
                    }
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (draggedOverDropZone !== null) {
                    handleDropZoneDrop(e, draggedOverDropZone);
                  }
                }}
                onMouseDown={(e) => {
                  // Prevent drag on button clicks
                  const target = e.target as HTMLElement;
                  if (target.tagName === "BUTTON" || target.closest("button")) {
                    e.preventDefault();
                  }
                }}
                style={{
                  cursor: isDraggedItem ? "grabbing" : "grab",
                  padding: "6px 8px",
                  margin: "0",
                  borderRadius: "4px",
                  backgroundColor: "transparent",
                  border: "1px solid transparent",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.15s ease",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <Text
                  color={
                    getValues()[columnId as string]
                      ? "textDark"
                      : "textSecondary"
                  }
                >
                  <Icon icon="faGrip" size="sm" />
                </Text>
                <Box grow={1} align="center" justify="space-between">
                  <Text
                    size={14}
                    color={
                      getValues()[columnId as string]
                        ? "textDark"
                        : "textSecondary"
                    }
                  >
                    {humanReadableNames[columnId as string]}
                  </Text>
                  <IconButton
                    size="sm"
                    color={
                      getValues()[columnId as string]
                        ? "textDark"
                        : "textSecondary"
                    }
                    icon={
                      getValues()[columnId as string] ? "faEye" : "faEyeSlash"
                    }
                    onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      const currentValue = getValues()[columnId as string];
                      const newValues = {
                        ...getValues(),
                        [columnId]: !currentValue,
                      };
                      reset(newValues);
                    }}
                  />
                </Box>
              </div>
            </div>
          </React.Fragment>
        );
      })}

      {/* Show placeholder at the end if dragging to last position */}
      {(() => {
        const draggedIndex = draggedItem
          ? localColumnOrder.indexOf(draggedItem)
          : -1;
        const showEndPlaceholder =
          draggedItem &&
          draggedOverDropZone === localColumnOrder.length &&
          draggedIndex !== localColumnOrder.length - 1;

        return showEndPlaceholder ? (
          <div
            style={{
              padding: "6px 8px",
              margin: "0",
              borderRadius: "4px",
              backgroundColor: "var(--background-default-secondary)",
              border: "2px dashed var(--border-default-primary)",
              height: "36px",
              opacity: 0.5,
            }}
          />
        ) : null;
      })()}
    </Form>
  );
};
